import qs from 'qs';
import axios from 'axios';
import get from 'lodash/get';
import merge from 'lodash/merge';
import Logger from 'observability/Logger';
import { sanitizeError } from 'utils/sanitizeError';
import catchInvalidatedTokenError from './catchInvalidatedTokenError';

export const CLIENT_VERSION = '0.0.1';
export const API_BASE_URL = 'https://api.splashthat.com';

/**
 * A constant that indicates how long a successful OPTIONS request should be
 * cached by the server. This is set to a max of 3600 (1 hour) on the server
 * side, but we need to pass the Access-Control-Max-Age header for it to cache.
 *
 * @var {int}
 */
export const ACCESS_CONTROL_MAX_AGE = 3600;
export const HTTP_TOO_MANY_REQUESTS = 429;
export const UNAUTHORIZED = 401;
export const TOO_MANY_REQUESTS_MAX_RETRIES = 5;
export const RETRY_DELAY_MULTIPLIER_MILLISECONDS = 1000;

const LOG = new Logger('SplashAPIClient');

export const INVALID_GRANT_ERROR = 'invalid_grant';
export const TOKEN_EXPIRED_ERROR = 'token expired';

const checkResponseCode = (response, url, method = 'GET') => {
    if (response.exp || response.error || (response.meta && response.meta.code >= 400 && response.meta.code !== 500)) {
        LOG.error({ splash_request_details: { url, method, error: response } });
        throw response;
    }

    // LOG.info({ splash_request_details: { url, method, success: response?.success } });
    return response;
};

/**
 * Splash API Client
 * =================
 *
 * Make Splash API requests!
 */
export default class SplashApiClient {
    static get VERSION() {
        return CLIENT_VERSION;
    }

    /**
     * Creates a SplashApiClient instance
     *
     * @param  {Object} [options] - API client options
     * @param  {string} [options.baseUrl] - API base URL
     * @param  {string} [options.accessToken] - API access token for authenticated requests
     * @param  {string} [options.fetch] - fetch implementation
     * @param  {boolean} [options.retry] - whether to refresh the access token on failure
     */
    constructor({ baseUrl = API_BASE_URL, accessToken = false, fetch = axios, retry = true } = {}) {
        // Make sure we don't have a trailing slash
        this.baseUrl = baseUrl.replace(/(.*?)\/$/, '$1');
        this.accessToken = accessToken;
        this.retry = retry;
        this.fetch = fetch;
    }

    /**
     * Does a fetch that handles expired access token errors
     *
     * If the request returns a 401 indicating an invalid grant, the client will refresh its access
     * token and retry the request afterwards
     *
     * @param  {string} url - URL to fetch
     * @param  {object} options - fetch options
     * @param  {boolean} [retry] - whether to refresh the access token on failure
     * @param  {int} [retryAttempt] - how many times have we retried
     * @param  {int} [retryDelayMultiplier] - how should we back-off our rate limit retries
     * @return {Promise} resolves with the fetch result
     */
    fetchWithRetry(
        url,
        options,
        retry = this.retry,
        retryAttempt = 0,
        retryDelayMultiplier = RETRY_DELAY_MULTIPLIER_MILLISECONDS,
    ) {
        const { omitAccessToken, ...restOptions } = options;

        let opts = merge(this.defaultOptions(), restOptions);
        if (!omitAccessToken) {
            opts.headers = this.addAccessTokenHeader(opts.headers);
        }

        let promise = this.fetch({
            url,
            ...opts,
        }).then((response) => response.data);

        if (retry) {
            promise = promise.catch((err) => {
                if (__DEVELOPMENT__) {
                    if (err.message && err.message.match(/network/i)) {
                        // eslint-disable-next-line no-console, max-len
                        console.error(
                            `Hi there! If you're seeing this message, it probably means that you need to accept the self-signed certificate from ${this.baseUrl} to make the local Splash API work in your browser!`,
                        );
                        throw err;
                    }
                }
                const errorStatus = get(err, 'response.status');
                if (errorStatus === HTTP_TOO_MANY_REQUESTS && retryAttempt < TOO_MANY_REQUESTS_MAX_RETRIES) {
                    return new Promise((resolve) => {
                        setTimeout(resolve, retryAttempt * retryDelayMultiplier);
                    }).then(() => this.fetchWithRetry(url, opts, retry, retryAttempt + 1, retryDelayMultiplier));
                }

                const errorCode = err?.response?.data?.error || err?.response?.data?.exp;
                const shouldErrorCodeTriggerAccessTokenRefresh = [INVALID_GRANT_ERROR, TOKEN_EXPIRED_ERROR].includes(
                    errorCode,
                );
                const isUnauthorizedResponse = errorStatus === UNAUTHORIZED;

                if (isUnauthorizedResponse && shouldErrorCodeTriggerAccessTokenRefresh) {
                    return this.refreshAccessToken()
                        .then(() => {
                            opts = merge(this.defaultOptions(), options);
                            opts.headers = this.addAccessTokenHeader(opts.headers);

                            return this.fetch({
                                url,
                                ...opts,
                            });
                        })
                        .then((response) => response.data)
                        .catch(() => {
                            throw sanitizeError(err.response);
                        });
                }
                catchInvalidatedTokenError(err.response);
                throw sanitizeError(err.response);
            });
        }

        return promise.then((response) => checkResponseCode(response, url, options?.method));
    }

    /**
     * Generates default fetch options that include the base URL and access token header
     *
     * @return {Object} default options object
     */
    defaultOptions() {
        const options = {
            headers: {
                /* eslint-disable quote-props */
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Access-Control-Max-Age': ACCESS_CONTROL_MAX_AGE,
                /* eslint-enable quote-props */
            },
        };

        return options;
    }

    /**
     * Refreshes the access token used for requests
     *
     * @return {Promise} resolves with the response data
     */
    refreshAccessToken() {
        if (!this._refreshAccessToken) {
            this._refreshAccessToken = this.fetch({
                url: '/users/refreshToken',
                ...this.defaultOptions(),
                withCredentials: true,
                credentials: 'same-origin',
            })
                .then((response) => response.data)
                .then(
                    (resp) => {
                        this._refreshAccessToken = null;
                        const accessToken = resp && resp.data && resp.data.access_token;
                        if (accessToken) {
                            this.accessToken = accessToken;
                            return accessToken;
                        }
                        throw new Error("Couldn't refresh access token");
                    },
                    (err) => {
                        this._refreshAccessToken = null;
                        throw err;
                    },
                );
        }
        return this._refreshAccessToken;
    }

    /**
     * Generates a full API URL from the given path
     *
     * @param  {string} apiPath - relative path on the API
     * @return {string} full API URL
     */
    apiUrl(apiPath) {
        // Make sure we don't double the slash on the API path
        return `${this.baseUrl}/${apiPath.replace(/^\/?(.*)/, '$1')}`;
    }

    /**
     * @param {string} url
     * @return {string} - url with access token if it didn't have it already.
     */
    addAccessToken(url) {
        if (this.accessToken && url.indexOf('access_token=') === -1) {
            const delimiter = url.indexOf('?') === -1 ? '?' : '&';
            return `${url}${delimiter}access_token=${this.accessToken}`;
        }
        return url;
    }

    /**
     * Given a set of headers, returns a new set of headers with the access
     * @param headers
     * @param accessToken
     * @returns {*&{Authorization: string}}
     */
    addAccessTokenHeader(headers) {
        return {
            ...headers,
            ...(this.accessToken && { Authorization: `Bearer ${this.accessToken}` }),
        };
    }

    /**
     * Does a GET request to the Splash API
     *
     * @param  {string} apiPath - relative path on the API
     * @param  {Object} params - query parameters sent with the request
     * @param  {Object} [options] - any additional options to set for the request
     * @return {Promise} resolves with the response body
     */
    get(apiPath, params, options = {}) {
        let url = this.apiUrl(apiPath);
        const queryString = qs.stringify(params);
        const { retry, ...restOptions } = options;

        let queryParamDelimiter = '?';
        if (url.indexOf('?') > -1) {
            queryParamDelimiter = '&';
        }
        queryString && (url += `${queryParamDelimiter}${queryString}`);
        return this.fetchWithRetry(
            url,
            {
                qs: params,
                ...restOptions,
            },
            retry,
        );
    }

    /**
     * Does a POST request to the Splash API
     *
     * @param  {string} apiPath - relative path on the API
     * @param  {Object} params - data send with the request
     * @param  {Object} [options] - any additional options to set for the request
     * @return {Promise} resolves with the response body
     */
    post(apiPath, params, options = {}) {
        const url = this.apiUrl(apiPath);
        const { retry, ...restOptions } = options;
        return this.fetchWithRetry(
            url,
            {
                method: 'POST',
                data: JSON.stringify(params),
                ...restOptions,
            },
            retry,
        );
    }

    /**
     * Does a PUT request to the Splash API
     *
     * @param  {string} apiPath - relative path on the API
     * @param  {Object} params - data send with the request
     * @param  {Object} [options] - any additional options to set for the request
     * @return {Promise} resolves with the response body
     */
    put(apiPath, params, options = {}) {
        const url = this.apiUrl(apiPath);
        const { retry, ...restOptions } = options;
        return this.fetchWithRetry(
            url,
            {
                method: 'PUT',
                data: JSON.stringify(params),
                ...restOptions,
            },
            retry,
        );
    }

    /**
     * Does a PATCH request to the Splash API
     *
     * @param  {string} apiPath - relative path on the API
     * @param  {Object} params - data send with the request
     * @param  {Object} [options] - any additional options to set for the request
     * @return {Promise} resolves with the response body
     */
    patch(apiPath, params, options = {}) {
        const url = this.apiUrl(apiPath);
        const { retry, ...restOptions } = options;
        return this.fetchWithRetry(
            url,
            {
                method: 'PATCH',
                data: JSON.stringify(params),
                ...restOptions,
            },
            retry,
        );
    }

    /**
     * Does a DELETE request to the Splash API
     *
     * @param  {string} apiPath - relative path on the API
     * @param  {Object} params - data send with the request
     * @param  {Object} [options] - any additional options to set for the request
     * @return {Promise} resolves with the response body
     */
    delete(apiPath, params, options = {}) {
        const url = this.apiUrl(apiPath);
        const { retry, ...restOptions } = options;
        return this.fetchWithRetry(
            url,
            {
                method: 'DELETE',
                data: JSON.stringify(params),
                ...restOptions,
            },
            retry,
        );
    }

    /**
     * Post a file using a given url
     * @param {string} url - Url to post the file
     * @param {Blob} file - File to upload
     * @param {Object} params extra params you wan to include
     * @param {Object} options options for the request
     */
    postFile(url, file, params = {}, options = {}) {
        return this.fetchFile(url, file, params, {
            ...options,
            method: 'POST',
        });
    }

    /**
     * Put a file using a given url
     * @param {string} url - Url to post the file
     * @param {Blob} file - File to upload
     * @param {Object} params extra params you wan to include
     * @param {Object} options options for the request
     */
    putFile(url, file, params = {}, options = {}) {
        LOG.warn(
            'There is a known issue uploading files with PUT method, use POST instead: ' +
                'https://github.com/symfony/symfony/issues/9226',
        );
        return this.fetchFile(url, file, params, { ...options, method: 'PUT' });
    }

    fetchFile(url, files, params = {}, options = {}) {
        const apiUrl = this.apiUrl(url);
        const data = new FormData();

        const headers = options.headers || [];

        // eslint-disable-next-line no-param-reassign
        options.headers = this.addAccessTokenHeader(headers);

        if (Array.isArray(files)) {
            files.forEach((file, i) => {
                data.append(`file[${i}]`, file);
            });
        } else {
            data.append('file', files);
        }

        Object.keys(params).forEach((key) => data.append(key, params[key]));

        return this.fetch({
            url: apiUrl,
            data,
            ...options,
        }).then((response) => response.data);
    }
}
